import {
  ButtonERP,
  ButtonFilterERP,
  OptionsMenuStorybook,
  SearchInput,
} from "@finanedi.es/storybook"
import { faFileExcel, faTrashCan } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CheckboxProps } from "antd"
import { useContactosEtiquetas } from "api/Contacto.hook"
import {
  ContactoByFiltersQueryParamsProps,
  getContactosExportar,
} from "api/Contacto.service"
import { useDownloadFileContext } from "common/context/DownloadFileProvider"
import notification from "common/utils/notification"
import { AnimatePresence } from "framer-motion"
import { Checkbox } from "primereact/checkbox"
import { Dialog } from "primereact/dialog"
import { Skeleton } from "primereact/skeleton"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ContactosTableProps } from "./constants/ContactosFilters"
import useContactos from "./hooks/useContactos"

export const FilterMenu = ({
  serviceQuery,
  sortingCriteria,
  skipCount,
  maxResultCount,
  selectedContactos,
  resetPagination,
  openVulkDeleteModal,
  contactosFilters,
  setContactosFilters,
}: {
  serviceQuery: Dispatch<SetStateAction<ContactoByFiltersQueryParamsProps>>
  sortingCriteria?: ContactoByFiltersQueryParamsProps["SortingCriteria"]
  skipCount?: ContactoByFiltersQueryParamsProps["SkipCount"]
  maxResultCount?: ContactoByFiltersQueryParamsProps["MaxResultCount"]
  selectedContactos: Array<object>
  resetPagination?: () => void
  openVulkDeleteModal?: () => void
  contactosFilters: ContactosTableProps
  setContactosFilters: Dispatch<SetStateAction<ContactosTableProps>>
}) => {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchEtiqueta, setSearchEtiqueta] = useState("")

  const handleSetContactosFilter = <K extends keyof ContactosTableProps>(
    key: K,
    value: ContactosTableProps[K]
  ) => {
    const updateFilter = {
      ...contactosFilters,
      [key]: value,
    }
    setContactosFilters(updateFilter)
  }

  const openFilters = () => {
    setMenuOpen(true)
  }
  const queryFixer = (queryParams: ContactoByFiltersQueryParamsProps) => {
    const facturasParams = {
      Cif: queryParams.Cif,
      Direccion: queryParams.Direccion,
      Email: queryParams.Email,
      EmpresaId: queryParams.EmpresaId,
      Etiquetas: queryParams.Etiquetas,
      IsExportEnabled: queryParams.IsExportEnabled,
      Keyword: queryParams.Keyword,
      RazonSocial: queryParams.RazonSocial,
      Telefono: queryParams.Telefono,
      MaxResultCount: queryParams.MaxResultCount,
      SkipCount: queryParams.SkipCount,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "",
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(facturasParams).filter(
        ([_key, value]) => value || value === 0 // eslint-disable-line @typescript-eslint/no-unused-vars
      )
    )
    return fixedQueryParams
  }
  const query = {
    Etiquetas: contactosFilters?.etiquetasSelected,
    SortingCriteria: sortingCriteria,
    SkipCount: skipCount,
    MaxResultCount: maxResultCount,
    Keyword: contactosFilters?.keyword,
  }
  const handleQuery = () => {
    serviceQuery(queryFixer(query))
  }
  const handleApplyFilters = () => {
    resetPagination?.()
    handleQuery()
    setMenuOpen(false)
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }
  const handleResetFilters = () => {
    serviceQuery(
      queryFixer({
        Keyword: contactosFilters?.keyword ?? "",
      })
    )
    const updateFilters = {
      ...contactosFilters,
      etiquetasSelected: [],
    }
    setContactosFilters(updateFilters)
    resetPagination?.()
  }

  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sortingCriteria?.sort,
    sortingCriteria?.property,
    contactosFilters?.keyword,
    contactosFilters?.etiquetasSelected,
    skipCount,
    maxResultCount,
  ])
  // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
  const { toggleShowCreateContacto } = useContactos()
  const [exportLoading, setExportLoading] = useState(false)
  const { handleDirectDownloadExcel } = useDownloadFileContext()

  const handleDownloadPdf = async () => {
    try {
      setExportLoading(true)
      const resp = await getContactosExportar()
      await handleDirectDownloadExcel(resp, `${t("contactos.title")}.xlsx`)
      setExportLoading(false)
      notification.errorServidor({
        body: `${t("notification.excelDownloadSuccess")}`,
      })
    } catch (error) {
      setExportLoading(false)
      notification.errorServidor({
        body: `${t("notification.incorrectThenMore")}`,
      })
    }
  }
  const {
    contactosEtiquetas: etiquetas,
    contactosEtiquetasIsLoading: etiquetasIsLoading,
  } = useContactosEtiquetas()

  const onSearchEtiquetas = (value: string) => {
    setSearchEtiqueta(value ?? null)
  }

  const appliedFiltersTags = [
    {
      label: t("filtros.contactos.labelLabel"),
      value:
        contactosFilters?.etiquetasSelected?.length > 0
          ? `(${contactosFilters?.etiquetasSelected?.length})`
          : undefined,
      action: () => {
        handleSetContactosFilter("etiquetasSelected", [])
      },
    },
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  return (
    <>
      <div className='tw-bg-white !tw-px-8 tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-items-center tw-pb-4 tw-pt-6 tw-shadow-3xl tw-rounded-t-xl'>
        <p className='tw-whitespace-nowrap tw-text-2xl tw-text-text tw-my-auto'>
          {t("contactos.title")}
        </p>
        <div className='tw-flex tw-w-full tw-justify-center lg:tw-justify-end'>
          <div className='!tw-w-fit tw-flex tw-items-center !tw-flex-row tw-justify-end tw-flex-wrap'>
            <div className='tw-grid tw-gap-3 lg:tw-flex lg:tw-flex-row tw-items-center tw-w-fit'>
              <div className='tw-flex tw-flex-row tw-gap-3'>
                {selectedContactos?.length > 0 && (
                  <ButtonERP
                    customize={{
                      size: "md",
                      type: "transparent",
                    }}
                    icon={
                      <FontAwesomeIcon
                        icon={faTrashCan}
                        className='!tw-text-danger'
                      />
                    }
                    onClick={openVulkDeleteModal}
                  />
                )}
                <ButtonFilterERP
                  isActive={appliedFilters.length > 0}
                  numberActive={appliedFilters?.length}
                  onClick={openFilters}
                  deleteFilter={handleResetFilters}
                  filterButtonLabel={`${t("filtros.title")}`}
                />
              </div>
              <SearchInput
                placeholder={`${t("contactos.placeholderSearch")}`}
                onChange={(ev: string) => {
                  handleSetContactosFilter("keyword", ev)
                }}
                inputValue={contactosFilters?.keyword || ""}
                className='tw-mb-1 sm:tw-mb-0 lg:tw-mx-1 tw-h-10 tw-w-full lg:tw-w-72'
              />
              <div className='tw-grid tw-grid-cols-3 xl:tw-flex tw-gap-3'>
                <ButtonERP
                  customize={{
                    size: "md",
                    type: "secondary",
                  }}
                  label={`${t("contactos.labelNew")}`}
                  onClick={() => toggleShowCreateContacto()}
                />
                <OptionsMenuStorybook
                  id='contactos-options-menu'
                  items={[
                    {
                      title: `${
                        !exportLoading
                          ? t("contactos.exportExcel")
                          : t("contactos.isExport")
                      }`,
                      hasRedirect: true,
                      action: () => handleDownloadPdf(),
                      isLoading: exportLoading,
                      icon: faFileExcel,
                    },
                  ]}
                  position='right'
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        <Dialog
          breakpoints={{ "960px": "75vw" }}
          className='p-dialog-right tw-select-none'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='tw-w-full tw-border-t-2 tw-pt-6'>
              <ButtonERP
                customize={{
                  type: "primary",
                  size: "md",
                }}
                className='!tw-w-full'
                label={`${t("filtros.labelApply")}`}
                onClick={handleApplyFilters}
              />
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='tw-flex tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("filterMenu.appliedTag.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("filterMenu.appliedTag.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={`${filter.label}-${filter.value}`}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("filtros.contactos.labelLabel")}
              </span>
            </div>
            <div className='tw-mb-4'>
              <SearchInput
                placeholder={`${t("filtros.contactos.placeholderSearch")}`}
                onChange={(ev: string) => {
                  onSearchEtiquetas(ev)
                }}
                inputValue={searchEtiqueta}
                className='tw-h-10 tw-w-full tw-mb-3'
              />
              {etiquetasIsLoading ? (
                <Skeleton className='!tw-h-1/2 !tw-w-full' />
              ) : (
                etiquetas?.items
                  ?.filter((etiqueta) =>
                    etiqueta
                      .toLowerCase()
                      .includes(searchEtiqueta?.toLowerCase() || "")
                  )
                  ?.map((option, index) => (
                    <div
                      key={`key-${index}`} // eslint-disable-line
                      className='tw-mb-2 tw-flex'
                    >
                      <Checkbox
                        inputId={option}
                        value={option}
                        checked={Boolean(
                          contactosFilters?.etiquetasSelected?.includes(option)
                        )}
                        onChange={(event: CheckboxProps) => {
                          const newEtiquetas = event.checked
                            ? [
                                ...(contactosFilters?.etiquetasSelected || []),
                                event.value,
                              ]
                            : contactosFilters?.etiquetasSelected?.filter(
                                (etiqueta: string) => etiqueta !== event.value
                              )
                          handleSetContactosFilter(
                            "etiquetasSelected",
                            newEtiquetas
                          )
                        }}
                      />
                      <label
                        htmlFor={option}
                        className='tw-my-auto tw-ml-2 tw-truncate'
                      >
                        {option}
                      </label>
                    </div>
                  ))
              )}
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}
